import React from 'react';
import { Loader2 } from 'lucide-react';
import './Button.css';

const baseClasses = 'vylo-button isolate inline-flex select-none items-center justify-center gap-2 overflow-hidden font-semibold transition-[transform,box-shadow,background-color,border-color,color,opacity] duration-150 ease-out focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus-ring-strong)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)] disabled:cursor-not-allowed motion-reduce:transition-none';

const variantClasses = {
  primary: 'vylo-button--primary',
  secondary: 'vylo-button--secondary',
  ghost: 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)]',
  unstyled: 'text-inherit',
  danger: 'border border-[var(--color-danger-button-border)] bg-[var(--color-danger-button-bg)] text-[var(--color-danger-button-text)] hover:bg-[var(--color-danger-button-bg-hover)] hover:text-[var(--color-danger-button-text)]',
  dangerSolid: 'bg-rose-600 text-white shadow-lg shadow-rose-500/25 hover:bg-rose-500 disabled:bg-rose-950/45 disabled:text-rose-200 disabled:shadow-none'
};

const sizeClasses = {
  sm: 'h-11 min-h-11 px-4 text-sm rounded-[5px]',
  md: 'h-12 px-5 text-sm rounded-[5px]',
  lg: 'h-12 px-6 text-base rounded-[5px]',
  xl: 'min-h-14 px-6 py-4 text-lg rounded-[5px]',
  iconSm: 'h-9 w-9 min-h-9 min-w-9 rounded-full p-0 text-sm',
  icon: 'h-11 w-11 min-h-11 min-w-11 rounded-[5px] p-0 text-sm',
  none: ''
};

const Button = React.forwardRef(({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  type = 'button',
  disabled = false,
  loading = false,
  loadingLabel = 'Working…',
  pressed,
  children,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...props
}, ref) => {
  const loadingLabelId = React.useId();
  const needsLoadingName = loading && ariaLabel == null && ariaLabelledBy == null;
  const hasTextLoadingLabel = typeof loadingLabel === 'string';
  const isDisabled = disabled || loading;
  const state = loading ? 'loading' : isDisabled ? 'disabled' : pressed ? 'pressed' : 'idle';
  const classes = [
    baseClasses,
    variantClasses[variant] || variantClasses.primary,
    sizeClasses[size] ?? sizeClasses.md,
    pressed && variant !== 'unstyled' ? 'ring-2 ring-[var(--color-focus-ring-strong)]' : '',
    fullWidth ? 'w-full' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-label={needsLoadingName && hasTextLoadingLabel ? loadingLabel : ariaLabel}
      aria-labelledby={needsLoadingName && !hasTextLoadingLabel ? loadingLabelId : ariaLabelledBy}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      aria-pressed={typeof pressed === 'boolean' ? pressed : undefined}
      data-variant={variantClasses[variant] ? variant : 'primary'}
      data-state={state}
      data-loading={loading ? 'true' : undefined}
      className={classes}
      {...props}
    >
      {loading ? null : (
        <span className="relative z-[1] inline-flex min-w-0 items-center justify-center gap-2 whitespace-nowrap">
          {children}
        </span>
      )}
      {loading ? (
        <span
          className="relative z-[1] inline-flex min-w-max items-center justify-center gap-2 whitespace-nowrap"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <Loader2 data-testid="button-loading-spinner" aria-hidden="true" className="h-4 w-4 shrink-0 motion-safe:animate-spin" />
          <span id={loadingLabelId}>{loadingLabel}</span>
        </span>
      ) : null}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
